import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from 'react-query';
import { ProductService } from '../services/ProductService';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  MenuItem,
  Box,
  Alert,
  CircularProgress,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  Input,
  IconButton,
  useTheme
} from '@mui/material';
import { Delete as DeleteIcon, Save as SaveIcon, AddPhotoAlternate, CloudUpload, DeleteForever } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useAuth } from '../contexts/AuthContext';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage, auth } from '../config/firebase';
import { useTranslation } from 'react-i18next';

const categories = [
  { value: 'engine', label: 'Engine Parts', icon: '🔧' },
  { value: 'transmission', label: 'Transmission', icon: '⚙️' },
  { value: 'brakes', label: 'Brakes', icon: '🛑' },
  { value: 'suspension', label: 'Suspension', icon: '🔩' },
  { value: 'electrical', label: 'Electrical', icon: '⚡' },
  { value: 'body', label: 'Body Parts', icon: '🚗' }
];

const conditions = ['New', 'Used', 'Refurbished'];

export default function EditProduct() {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const { currentUser } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    price: '',
    category: '',
    brand: '',
    model: '',
    year: new Date().getFullYear(),
    condition: '',
    stock: '',
    location: ''
  });
  const [newImages, setNewImages] = useState([]);
  const [imagesToDelete, setImagesToDelete] = useState([]);
  const { t } = useTranslation();

  // Fetch product data
  const { data: product, isLoading, error } = useQuery(
    ['product', id],
    () => ProductService.getProductById(id),
    {
      onSuccess: (data) => {
        // Only allow editing if current user is the seller
        if (data.sellerId !== currentUser?.uid) {
          navigate('/');
          enqueueSnackbar('You are not authorized to edit this product', { variant: 'error' });
        }
        setFormData(data);
      }
    }
  );

  // Update mutation with image handling
  const updateMutation = useMutation(
    async (updates) => {
      // First, upload any new images
      const newImageUrls = await Promise.all(
        newImages.map(async (image) => {
          const storageRef = ref(storage, `products/${Date.now()}_${image.name}`);
          const metadata = {
            contentType: image.type,
            cacheControl: 'public,max-age=3600'
          };
          await uploadBytes(storageRef, image, metadata);
          return getDownloadURL(storageRef);
        })
      );

      // Filter out deleted images and add new ones
      const currentImages = updates.images.filter(
        (img) => !imagesToDelete.includes(img)
      );
      
      const updatedProduct = {
        ...updates,
        images: [...currentImages, ...newImageUrls]
      };

      // Delete the old images from storage
      await Promise.all(
        imagesToDelete.map(async (imageUrl) => {
          try {
            const imageRef = ref(storage, imageUrl);
            await deleteObject(imageRef);
          } catch (error) {
            console.error('Error deleting image:', error);
          }
        })
      );

      return ProductService.updateProduct(id, updatedProduct);
    },
    {
      onSuccess: () => {
        enqueueSnackbar(t('editProduct.updateSuccess'), { variant: 'success' });
        navigate('/profile');
      },
      onError: (error) => {
        enqueueSnackbar(t('editProduct.updateError'), { variant: 'error' });
      }
    }
  );

  // Delete mutation
  const deleteMutation = useMutation(
    () => ProductService.deleteProduct(id),
    {
      onSuccess: () => {
        enqueueSnackbar(t('editProduct.deleteSuccess'), { variant: 'success' });
        navigate('/profile');
      },
      onError: (error) => {
        enqueueSnackbar(t('editProduct.deleteError'), { variant: 'error' });
      }
    }
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    updateMutation.mutate(formData);
  };

  const handleDelete = async () => {
    if (window.confirm(t('editProduct.deleteConfirm'))) {
      deleteMutation.mutate();
    }
  };

  const handleImageUpload = (event) => {
    const files = Array.from(event.target.files);
    const validFiles = files.filter(file => file.type.startsWith('image/'));
    setNewImages(prev => [...prev, ...validFiles]);
  };

  const handleRemoveNewImage = (index) => {
    setNewImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleRemoveExistingImage = (imageUrl) => {
    setImagesToDelete(prev => [...prev, imageUrl]);
  };

  const handleRestoreImage = (imageUrl) => {
    setImagesToDelete(prev => prev.filter(url => url !== imageUrl));
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error.message}
      </Alert>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 4 }}>
          <Typography variant="h4" gutterBottom>
            {t('editProduct.title')}
          </Typography>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleDelete}
          >
            {t('common.delete')}
          </Button>
        </Box>

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('editProduct.labels.title')}
                name="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label={t('editProduct.labels.description')}
                name="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label={t('editProduct.labels.price')}
                name="price"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label={t('editProduct.labels.category')}
                name="category"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                required
              >
                {categories.map((category) => (
                  <MenuItem key={category.value} value={category.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span>{category.icon}</span>
                      {t(`categories.${category.value.toLowerCase()}`)}
                    </Box>
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('editProduct.labels.brand')}
                name="brand"
                value={formData.brand}
                onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('editProduct.labels.model')}
                name="model"
                value={formData.model}
                onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label={t('editProduct.labels.year')}
                name="year"
                value={formData.year}
                onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label={t('editProduct.labels.condition')}
                name="condition"
                value={formData.condition}
                onChange={(e) => setFormData({ ...formData, condition: e.target.value })}
                required
              >
                {conditions.map((condition) => (
                  <MenuItem key={condition} value={condition}>
                    {t(`product.conditions.${condition.toLowerCase()}`)}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label={t('editProduct.labels.stock')}
                name="stock"
                value={formData.stock}
                onChange={(e) => setFormData({ ...formData, stock: e.target.value })}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('editProduct.labels.location')}
                name="location"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                required
              />
            </Grid>

            {/* Image Management Section */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                {t('editProduct.images.title')}
              </Typography>
              
              {/* Existing Images */}
              {formData.images?.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    {t('editProduct.images.currentImages')}
                  </Typography>
                  <ImageList cols={4} rowHeight={200} gap={8}>
                    {formData.images.map((imageUrl, index) => (
                      <ImageListItem 
                        key={imageUrl}
                        sx={{
                          opacity: imagesToDelete.includes(imageUrl) ? 0.5 : 1,
                          transition: 'opacity 0.3s'
                        }}
                      >
                        <img
                          src={imageUrl}
                          alt={`${t('product.image')} ${index + 1}`}
                          loading="lazy"
                          style={{ height: '100%', objectFit: 'cover' }}
                        />
                        <ImageListItemBar
                          actionIcon={
                            imagesToDelete.includes(imageUrl) ? (
                              <IconButton
                                sx={{ color: 'white' }}
                                onClick={() => handleRestoreImage(imageUrl)}
                                title={t('editProduct.images.restore')}
                              >
                                <CloudUpload />
                              </IconButton>
                            ) : (
                              <IconButton
                                sx={{ color: 'white' }}
                                onClick={() => handleRemoveExistingImage(imageUrl)}
                                title={t('editProduct.images.delete')}
                              >
                                <DeleteForever />
                              </IconButton>
                            )
                          }
                        />
                      </ImageListItem>
                    ))}
                  </ImageList>
                </Box>
              )}

              {/* New Images */}
              {newImages.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    {t('editProduct.images.newImages')}
                  </Typography>
                  <ImageList cols={4} rowHeight={200} gap={8}>
                    {newImages.map((file, index) => (
                      <ImageListItem key={index}>
                        <img
                          src={URL.createObjectURL(file)}
                          alt={`${t('product.newImage')} ${index + 1}`}
                          loading="lazy"
                          style={{ height: '100%', objectFit: 'cover' }}
                        />
                        <ImageListItemBar
                          actionIcon={
                            <IconButton
                              sx={{ color: 'white' }}
                              onClick={() => handleRemoveNewImage(index)}
                              title={t('editProduct.images.delete')}
                            >
                              <DeleteForever />
                            </IconButton>
                          }
                        />
                      </ImageListItem>
                    ))}
                  </ImageList>
                </Box>
              )}

              {/* Upload Button */}
              <Box sx={{ mb: 3 }}>
                <Input
                  accept="image/*"
                  id="image-upload"
                  multiple
                  type="file"
                  style={{ display: 'none' }}
                  onChange={handleImageUpload}
                />
                <label htmlFor="image-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<AddPhotoAlternate />}
                  >
                    {t('editProduct.images.addMore')}
                  </Button>
                </label>
                <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                  {t('editProduct.images.uploadLimit', {
                    count: 6 - (formData.images?.length - imagesToDelete.length + newImages.length)
                  })}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/profile')}
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<SaveIcon />}
                  disabled={updateMutation.isLoading}
                >
                  {updateMutation.isLoading ? t('editProduct.buttons.saving') : t('editProduct.buttons.save')}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Container>
  );
} 